import { useMemo } from 'react';
import { useLearningProgress } from '../../../hooks/useLearningProgress';

const RING_RADIUS = 50;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function parseHexColor(hex) {
  if (!hex || typeof hex !== 'string') return { r: 147, g: 105, b: 204 };
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length !== 6) return { r: 147, g: 105, b: 204 };
  const value = Number.parseInt(cleaned, 16);
  if (Number.isNaN(value)) return { r: 147, g: 105, b: 204 };
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function getTrackColor(hex, alpha = 0.22) {
  const { r, g, b } = parseHexColor(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatWatchTime(seconds) {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getSubjectWatchSeconds(subject, performance) {
  if (performance?.time_watched_seconds != null) {
    return performance.time_watched_seconds;
  }
  if (performance?.minutes_watched != null) {
    return Math.round(Number(performance.minutes_watched) * 60);
  }

  return (subject.chapters || []).reduce(
    (total, chapter) => total + (chapter.time_watched_seconds || 0),
    0
  );
}

function SubjectProgressCard({ name, color, watchSeconds, lessonsCompleted, lessonsTotal }) {
  const lessonProgress = lessonsTotal > 0 ? Math.min(lessonsCompleted / lessonsTotal, 1) : 0;
  const watchHint =
    lessonProgress === 0 && watchSeconds > 0 && lessonsTotal > 0
      ? Math.min(Math.max(watchSeconds / (lessonsTotal * 300), 0.06), 0.2)
      : 0;
  const progress = Math.max(lessonProgress, watchHint);
  const strokeDashoffset = RING_CIRCUMFERENCE * (1 - progress);
  const trackColor = getTrackColor(color);

  return (
    <article className="flex min-w-[220px] flex-1 flex-col rounded-2xl bg-white px-6 py-8 shadow-[0_2px_14px_rgba(0,0,0,0.07)]">
      <h3 className="text-center text-lg font-bold text-[#1B1B1B] sm:text-xl">{name}</h3>

      <div className="relative mx-auto mt-6 h-[160px] w-[160px]">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden>
          <circle
            cx="60"
            cy="60"
            r={RING_RADIUS}
            fill="none"
            stroke={trackColor}
            strokeWidth="14"
          />
          <circle
            cx="60"
            cy="60"
            r={RING_RADIUS}
            fill="none"
            stroke={color}
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[#1B1B1B]">
          <span className="text-[28px] font-bold leading-none sm:text-[32px]">
            {formatWatchTime(watchSeconds)}
          </span>
          <span className="mt-1 text-sm font-medium text-[#666666]">min</span>
        </div>
      </div>

      <p className="mt-5 text-center text-[15px] font-medium text-[#666666] sm:text-base">
        {lessonsCompleted}/{lessonsTotal} Lesson
      </p>
    </article>
  );
}

function ProgressSkeleton() {
  return (
    <div className="min-w-[220px] flex-1 animate-pulse rounded-2xl bg-white/70 px-6 py-8">
      <div className="mx-auto h-5 w-24 rounded bg-gray-200" />
      <div className="mx-auto mt-6 h-[148px] w-[148px] rounded-full bg-gray-200" />
      <div className="mx-auto mt-5 h-4 w-28 rounded bg-gray-200" />
    </div>
  );
}

function MyProgressSection({ categoryId }) {
  const { data, loading, error, subscriptionRequired } = useLearningProgress(
    categoryId,
    'this_week',
    Boolean(categoryId)
  );

  const subjects = useMemo(() => {
    const performanceById = new Map(
      (data?.performance?.subjects || []).map((subject) => [subject.subject_id, subject])
    );

    return (data?.subjects || []).map((subject) => {
      const performance = performanceById.get(subject.subject_id);
      return {
        id: subject.subject_id,
        name: subject.name,
        color: subject.color_code || performance?.color_code || '#9369CC',
        watchSeconds: getSubjectWatchSeconds(subject, performance),
        lessonsCompleted: subject.lessons_completed ?? 0,
        lessonsTotal: subject.lessons_total ?? 0,
      };
    });
  }, [data]);

  if (!categoryId) return null;

  return (
    <section className="bg-[#F3F0FF] px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold text-[#1B1B1B] sm:text-[32px]">My Progress</h2>

        {error && !subscriptionRequired && (
          <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
        )}

        {subscriptionRequired && !loading && (
          <p className="mt-4 rounded-lg bg-[#F3F0FF] px-4 py-3 text-sm font-medium text-[#9369CC]">
            Get an active subscription for this class to view your learning progress.
          </p>
        )}

        <div className="mt-8 flex gap-5 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden xl:grid xl:grid-cols-4 xl:overflow-visible">
          {loading && !subjects.length
            ? [1, 2, 3, 4].map((n) => <ProgressSkeleton key={n} />)
            : subjects.map((subject) => (
                <SubjectProgressCard
                  key={subject.id}
                  name={subject.name}
                  color={subject.color}
                  watchSeconds={subject.watchSeconds}
                  lessonsCompleted={subject.lessonsCompleted}
                  lessonsTotal={subject.lessonsTotal}
                />
              ))}
        </div>

        {!loading && !error && !subjects.length && (
          <p className="mt-6 text-center text-sm font-medium text-[#666666]">
            Start learning to see your progress here.
          </p>
        )}
      </div>
    </section>
  );
}

export default MyProgressSection;
